import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button, Select, Input, Modal, Textarea } from "@/components/kit";
import { fmtCt } from "@/lib/dates";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Txt } from "@/components/Txt";
import PersonPartnerships from "@/components/PersonPartnerships";
import PersonBricks from "@/components/PersonBricks";
import { SortControl, useSort } from "@/components/SortControl";
import { useQuickAdd } from "@/lib/quickAdd";
import ConfirmDelete from "@/components/ConfirmDelete";

// Only surface a server detail if it's a short, plain-text message (guards against
// a proxy timeout leaking a raw HTML error page into the toast).
const cleanDetail = (d) => (typeof d === "string" && d.length <= 200 && !/[<>]/.test(d) ? d : "");

function NewVisitorForm({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", organization: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter a name.");
    setBusy(true);
    try { const r = await api.post("/visitors", f); toast.success(`Added ${r.data.visitor.name}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add visitor"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New visitor" data-testid="new-visitor-form"
      footer={<Button data-testid="new-visitor-save" onClick={save} disabled={busy}>{busy ? "Adding…" : "Add visitor"}</Button>}>
      <div className="space-y-3">
        <p className="text-[13px] text-ink/60">Log a walk-in at the front desk. No membership needed — you can convert them later.</p>
        <div><label className="text-[13px] font-semibold block mb-1">Name</label><Input data-testid="new-visitor-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><label className="text-[13px] font-semibold block mb-1">Organization / business (optional)</label><Input data-testid="new-visitor-org" value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-[13px] font-semibold block mb-1">Phone (optional)</label><Input data-testid="new-visitor-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">Email (optional)</label><Input data-testid="new-visitor-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        </div>
      </div>
    </Modal>
  );
}

function VisitorDetail({ visitor, onClose }) {
  return (
    <div data-testid="visitor-detail">
      <button onClick={onClose} data-testid="visitor-detail-close" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-ink/70 hover:text-ink mb-3">
        <Icon name="ChevronLeft" className="w-4 h-4" /> Back to Visitors
      </button>
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold leading-tight">{visitor.name}</h1>
      <div className="text-[14px] text-ink/60 mb-6">{[visitor.phone, visitor.email].filter(Boolean).join(" · ") || "No contact on file"}</div>
      <div className="max-w-2xl space-y-4">
        <PersonPartnerships kind="visitor" id={visitor.visitor_id} />
        <PersonBricks kind="visitor" id={visitor.visitor_id} />
      </div>
    </div>
  );
}

const fmt = (s) => (s ? fmtCt(s) : "—");

function ConvertForm({ visitor, onClose, onDone }) {
  const [f, setF] = useState({ name: visitor.name || "", email: visitor.email || "", phone: visitor.phone || "", dob: "", account_type: "resident", pin: "", street: "", city: "", state: "", zip: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!/^\d{4}$/.test(f.pin)) return toast.error("Set a 4-digit PIN.");
    if (!f.street.trim() || !f.city.trim() || !f.state.trim() || !f.zip.trim()) return toast.error("A full home address is required.");
    setBusy(true);
    try {
      const r = await api.post("/membership/members", { ...f });
      const m = r.data.member || r.data;
      await api.post(`/visitors/${visitor.visitor_id}/converted`, { member_id: m.member_id });
      toast.success(`Created ${m.member_number}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create member"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Convert to member" data-testid="convert-form"
      footer={<Button data-testid="convert-save" onClick={save} disabled={busy}>{busy ? "Creating…" : "Create member"}</Button>}>
      <div className="space-y-3">
        <p className="text-[13px] text-ink/60">Their name and contact are carried over — just add the details a membership needs.</p>
        <div><label className="text-[13px] font-semibold block mb-1">Name</label><Input data-testid="convert-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-[13px] font-semibold block mb-1">Email</label><Input data-testid="convert-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">Phone</label><Input data-testid="convert-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-[13px] font-semibold block mb-1">Date of birth</label><Input type="date" data-testid="convert-dob" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">Account type</label>
            <Select data-testid="convert-type" value={f.account_type} onChange={(e) => setF({ ...f, account_type: e.target.value })}>
              <option value="resident">Resident</option><option value="business">Business</option><option value="organization">Organization</option><option value="church">Church</option>
            </Select></div>
        </div>
        <div><label className="text-[13px] font-semibold block mb-1">Street address</label><Input data-testid="convert-street" value={f.street} onChange={(e) => setF({ ...f, street: e.target.value })} /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-[13px] font-semibold block mb-1">City</label><Input data-testid="convert-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">State</label><Input data-testid="convert-state" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">ZIP</label><Input data-testid="convert-zip" value={f.zip} onChange={(e) => setF({ ...f, zip: e.target.value })} /></div>
        </div>
        <div><label className="text-[13px] font-semibold block mb-1">4-digit PIN</label><Input data-testid="convert-pin" inputMode="numeric" maxLength={4} value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "") })} /></div>
      </div>
    </Modal>
  );
}

const INVITE_TEMPLATES = [
  { id: "welcome", label: "Warm welcome", subject: "You're invited to join Rosemoor",
    body: (n) => `Hi ${n},\n\nWe loved having you at Rosemoor and we'd be thrilled to count you as a full member. It only takes a minute — just tap the button below and your name and email are already filled in.` },
  { id: "event", label: "Event follow-up", subject: "Thanks for coming — join Rosemoor",
    body: (n) => `Hi ${n},\n\nThank you for joining us at a recent Rosemoor event! Becoming a member keeps you in the loop on everything ahead. Tap below to create your account — we've pre-filled your details.` },
  { id: "nudge", label: "Quick nudge", subject: "Become a Rosemoor member",
    body: (n) => `Hi ${n},\n\nReady to make it official? Create your Rosemoor membership in under a minute — your name and email are already filled in.` },
];

function TemplatePicker({ onPick }) {
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="invite-templates">
      <span className="text-[13px] text-ink/55">Templates:</span>
      {INVITE_TEMPLATES.map((t) => (
        <button key={t.id} type="button" data-testid={`template-${t.id}`} onClick={() => onPick(t)} className="text-[13px] font-semibold px-3 py-1.5 rounded-full border border-ink/20 hover:bg-ink/5">{t.label}</button>
      ))}
    </div>
  );
}

function InviteForm({ visitor, onClose, onDone }) {
  const first = (visitor.name || "").split(" ")[0] || "there";
  const [subject, setSubject] = useState(INVITE_TEMPLATES[0].subject);
  const [message, setMessage] = useState(INVITE_TEMPLATES[0].body(first));
  const [busy, setBusy] = useState(false);
  const pick = (t) => { setSubject(t.subject); setMessage(t.body(first)); };
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/visitors/${visitor.visitor_id}/invite`, { subject, message, origin: window.location.origin });
      toast.success(`Invitation emailed to ${visitor.name}`); onDone();
    } catch (e) { toast.error(cleanDetail(e.response?.data?.detail) || "Could not send invite"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Invite to join" data-testid="invite-form"
      footer={<Button data-testid="invite-send" onClick={send} disabled={busy}>{busy ? "Sending…" : "Send invitation"}</Button>}>
      <div className="space-y-3">
        <p className="text-[13px] text-ink/60">Emails {visitor.name} at <span className="font-semibold">{visitor.email}</span>. A "Create your membership" button is added automatically, pre-filled with their name and email.</p>
        <TemplatePicker onPick={pick} />
        <div><label className="text-[13px] font-semibold block mb-1">Subject</label><Input data-testid="invite-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
        <div><label className="text-[13px] font-semibold block mb-1">Message</label><Textarea data-testid="invite-message" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} /></div>
      </div>
    </Modal>
  );
}

function BulkInviteForm({ visitors, onClose, onDone }) {
  const [subject, setSubject] = useState(INVITE_TEMPLATES[0].subject);
  const [message, setMessage] = useState(INVITE_TEMPLATES[0].body("there"));
  const [busy, setBusy] = useState(false);
  const pick = (t) => { setSubject(t.subject); setMessage(t.body("there")); };
  const send = async () => {
    setBusy(true);
    const results = await Promise.allSettled(visitors.map((v) =>
      api.post(`/visitors/${v.visitor_id}/invite`,
        { subject, message: message.replace(/\bthere\b/, (v.name || "there").split(" ")[0]), origin: window.location.origin })));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    setBusy(false);
    if (ok) toast.success(`Invitation emailed to ${ok} visitor${ok === 1 ? "" : "s"}`);
    if (ok < visitors.length) {
      const reason = cleanDetail(results.find((r) => r.status === "rejected")?.reason?.response?.data?.detail);
      toast.error(`${visitors.length - ok} could not be sent${reason ? `: ${reason}` : ""}`);
    }
    onDone();
  };
  return (
    <Modal open onClose={onClose} title={`Invite ${visitors.length} visitor${visitors.length === 1 ? "" : "s"}`} data-testid="bulk-invite-form"
      footer={<Button data-testid="bulk-invite-send" onClick={send} disabled={busy}>{busy ? "Sending…" : `Send ${visitors.length} invitation${visitors.length === 1 ? "" : "s"}`}</Button>}>
      <div className="space-y-3">
        <p className="text-[13px] text-ink/60">Each email greets the visitor by first name and includes a pre-filled "Create your membership" button. Only visitors with an email on file are included.</p>
        <TemplatePicker onPick={pick} />
        <div><label className="text-[13px] font-semibold block mb-1">Subject</label><Input data-testid="bulk-invite-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
        <div><label className="text-[13px] font-semibold block mb-1">Message</label><Textarea data-testid="bulk-invite-message" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} /></div>
      </div>
    </Modal>
  );
}

export default function Visitors() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [converting, setConverting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adding, setAdding] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkInviting, setBulkInviting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  // One-shot filter handed over from the dashboard "Repeat visitors" tile.
  const [repeatOnly, setRepeatOnly] = useState(() => { try { if (sessionStorage.getItem("rca_visitors_filter") === "repeat_no_join") { sessionStorage.removeItem("rca_visitors_filter"); return true; } } catch { /* ignore */ } return false; });
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDelete = async () => {
    const ids = [...selected];
    await Promise.all(ids.map((id) => api.delete(`/visitors/${id}`).catch(() => null)));
    toast.success(`${ids.length} visitor${ids.length === 1 ? "" : "s"} moved to Trash`);
    setSelected(new Set()); load();
  };
  const goMember = (mm) => { if (!mm) return; sessionStorage.setItem("rca_open_member", mm.member_id); window.dispatchEvent(new CustomEvent("rca:navigate", { detail: { section: "membership_directory" } })); };
  useQuickAdd("visitors", () => setAdding(true));
  const limit = 25;
  const [sort, updateSort] = useSort("visitors", "last_visit", "desc");
  const onSortChange = (field, direction) => { setSkip(0); updateSort(field, direction); };
  const load = useCallback(async () => {
    const r = await api.get("/visitors", { params: { limit, skip, sort: sort.field, dir: sort.dir, ...(q ? { q } : {}) } });
    setData(r.data);
  }, [q, skip, sort]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const onSearch = (v) => { setSkip(0); setQ(v); };
  // Same rule the dashboard tile counts: 2+ visits and not yet a member.
  const visItems = repeatOnly ? data.items.filter((v) => (v.visit_count >= 2) && !v.converted && !v.already_member) : data.items;

  if (detail) return <VisitorDetail visitor={detail} onClose={() => setDetail(null)} />;

  return (
    <div data-testid="visitors-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold"><Txt id="visitors.title" screen="Visitors">Visitors</Txt></h1>
        <div className="flex gap-2">
          <a data-testid="visitors-export" href={`${process.env.REACT_APP_BACKEND_URL}/api/visitors/export?q=${encodeURIComponent(q)}`} className="border border-ink/25 rounded-lg px-4 py-2 min-h-[44px] text-[15px] font-semibold inline-flex items-center gap-1.5"><Icon name="Download" className="w-4 h-4" /> Export</a>
          <Button data-testid="new-visitor-btn" onClick={() => setAdding(true)}><Icon name="Plus" className="w-4 h-4" /> New visitor</Button>
        </div>
      </div>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="visitors.subtitle" screen="Visitors">Everyone who's checked in as a visitor. Convert a returning face into a full member.</Txt></p>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Input data-testid="visitors-search" value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Search name, phone, email" className="max-w-md" />
        <SortControl testid="visitors-sort" field={sort.field} dir={sort.dir} onChange={onSortChange}
          options={[["last_visit", "Last visit"], ["name", "Name"], ["date_added", "Date added"], ["visits", "Visits"]]} />
      </div>
      {repeatOnly && (
        <div className="flex items-center gap-2 bg-gold/15 border border-gold/40 rounded-lg px-3 py-2 mb-4" data-testid="visitors-repeat-filter">
          <Icon name="Filter" className="w-4 h-4 text-ink/60" />
          <span className="text-[13px] font-semibold text-ink/75">Showing {visItems.length} repeat visitor(s) who haven't joined</span>
          <button className="ml-auto text-[13px] underline text-maroon" data-testid="visitors-repeat-clear" onClick={() => setRepeatOnly(false)}>Clear filter</button>
        </div>
      )}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-ink text-cream rounded-xl px-4 py-3 mb-4" data-testid="bulk-invite-bar">
          <span className="text-[14px] font-semibold">{selected.size} selected</span>
          <Button data-testid="bulk-invite-btn" onClick={() => setBulkInviting(true)} className="px-3 py-2 min-h-[38px] text-[13px]">Invite selected to join</Button>
          <Button variant="danger" data-testid="bulk-delete-btn" onClick={() => setConfirmDel(true)} className="px-3 py-2 min-h-[38px] text-[13px]">Delete</Button>
          <button data-testid="bulk-clear" onClick={() => setSelected(new Set())} className="text-[13px] underline text-cream/80 ml-auto">Clear</button>
        </div>
      )}
      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="visitors-list">
        {visItems.length === 0 && <div className="p-10 text-center text-ink/55">{repeatOnly ? "No repeat visitors who haven't joined." : "No visitors yet."}</div>}
        {visItems.map((v) => (
          <div key={v.visitor_id} data-testid={`visitor-row-${v.visitor_id}`} className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0">
            <div className="flex items-center gap-3 min-w-0">
              <input type="checkbox" data-testid={`select-${v.visitor_id}`} checked={selected.has(v.visitor_id)} onChange={() => toggle(v.visitor_id)} className="w-4 h-4 accent-maroon shrink-0" />
              <div className="min-w-0">
              <div className="text-[16px] font-semibold flex items-center gap-2">{v.name}{v.converted && <span className="text-[11px] px-2 py-0.5 rounded-full bg-olive/25 text-ink">Member</span>}{v.already_member && !v.converted && <button data-testid={`already-member-${v.visitor_id}`} onClick={() => goMember(v.member_match)} className="text-[11px] px-2 py-0.5 rounded-full bg-gold/40 text-ink hover:bg-gold/60 inline-flex items-center gap-1">Already a member <Icon name="ExternalLink" className="w-3 h-3" /></button>}{v.linked_org_id && <span className="text-[11px] px-2 py-0.5 rounded-full bg-gold/30 text-ink">Linked</span>}{v.invited_at && !v.converted && !v.already_member && <span className="text-[11px] px-2 py-0.5 rounded-full bg-ink/10 text-ink">Invited</span>}</div>
              {v.organization && <div className="text-[13px] text-dark-olive font-medium">{v.organization}</div>}
              <div className="text-[13px] text-ink/55">{[v.phone, v.email].filter(Boolean).join(" · ") || "No contact"} · {v.visit_count} visit{v.visit_count === 1 ? "" : "s"} · last {fmt(v.last_visit)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="secondary" data-testid={`visitor-detail-btn-${v.visitor_id}`} onClick={() => setDetail(v)} className="px-3 py-2 min-h-[40px] text-[14px]">Details</Button>
              {!v.converted && !v.already_member && v.email && <Button variant="secondary" data-testid={`invite-btn-${v.visitor_id}`} onClick={() => setInviting(v)} className="px-3 py-2 min-h-[40px] text-[14px]">{v.invited_at ? "Re-invite" : "Invite to join"}</Button>}
              {!v.converted && !v.already_member && <Button data-testid={`convert-btn-${v.visitor_id}`} onClick={() => setConverting(v)} className="px-3 py-2 min-h-[40px] text-[14px]">Convert to member</Button>}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4" data-testid="visitors-pagination">
        <span className="text-[14px] text-ink/60">Showing {data.items.length ? skip + 1 : 0}–{skip + data.items.length} of {data.total}</span>
        <div className="flex gap-2">
          <Button variant="secondary" data-testid="visitors-page-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="px-4 py-2 min-h-[40px] text-[14px]">Previous</Button>
          <Button variant="secondary" data-testid="visitors-page-next" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="px-4 py-2 min-h-[40px] text-[14px]">Next</Button>
        </div>
      </div>
      {converting && <ConvertForm visitor={converting} onClose={() => setConverting(null)} onDone={() => { setConverting(null); load(); }} />}
      {adding && <NewVisitorForm onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {inviting && <InviteForm visitor={inviting} onClose={() => setInviting(null)} onDone={() => { setInviting(null); load(); }} />}
      {bulkInviting && <BulkInviteForm visitors={data.items.filter((v) => selected.has(v.visitor_id))} onClose={() => setBulkInviting(false)} onDone={() => { setBulkInviting(false); setSelected(new Set()); load(); }} />}
      <ConfirmDelete open={confirmDel} onClose={() => setConfirmDel(false)} onConfirm={bulkDelete} title={`Delete ${selected.size} visitor${selected.size === 1 ? "" : "s"}?`} message="This moves the selected visitors to Trash, where they can be restored for 30 days before they are permanently removed." />
    </div>
  );
}
